import type { KryspaceControlBridgeStructure } from '../../../types/kryspaceControlBridgeStructure';
import type { McDomainKey } from '../missionControl/mcDomainVgr';
import type { GuidanceTreeNode } from '../personalLayer/types';

const COMPANY_ARTIFACT_PREFIX = 'ca-';
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const MC_DOMAIN_KEYS: ReadonlySet<string> = new Set(['business', 'customer', 'stakeholder']);

export function isSyntheticMcDomainNodeId(id: string): boolean {
  return /-mc-(business|stakeholder|customer)$/i.test(id.trim());
}

/** Mission Control synthetic domain type refs (slug + PascalCase identity). */
const SYNTHETIC_MC_DOMAIN_TYPE_REFS = new Set(
  [
    'pt-bridge-session-mc-domain',
    'BridgeSessionMcDomain',
    'bridgesessionmcdomain',
  ].map((s) => s.toLowerCase())
);

export function isSyntheticMcDomainTypeRef(artifactTypeRef: string | undefined | null): boolean {
  const raw = (artifactTypeRef ?? '').trim().toLowerCase();
  if (!raw) return false;
  if (SYNTHETIC_MC_DOMAIN_TYPE_REFS.has(raw)) return true;
  return raw.includes('bridge-session-mc-domain') || raw.includes('bridgesessionmcdomain');
}

export function isCompanyArtifactInstanceId(id: string): boolean {
  const trimmed = id.trim();
  if (!trimmed) return false;
  if (trimmed.startsWith(COMPANY_ARTIFACT_PREFIX)) return true;
  return UUID_PATTERN.test(trimmed);
}

export function isMcDomainKey(value: string | null | undefined): value is McDomainKey {
  return Boolean(value && MC_DOMAIN_KEYS.has(value.trim().toLowerCase()));
}

/** Prefer camelCase ``mcDomainKey``; accept snake_case from orient / thin-read. */
export function nodeMcDomainKey(
  node: { mcDomainKey?: string; mc_domain_key?: string }
): McDomainKey | undefined {
  const raw = (node.mcDomainKey ?? node.mc_domain_key ?? '').trim().toLowerCase();
  return isMcDomainKey(raw) ? raw : undefined;
}

function domainNodeCandidate(node: {
  artifactInstanceId?: string;
  filterScope?: string;
  key?: string;
  artifactTypeRef?: string;
}): string | undefined {
  const candidate = node.artifactInstanceId ?? node.filterScope ?? node.key;
  if (!candidate || isSyntheticMcDomainNodeId(candidate)) {
    return undefined;
  }
  if (isSyntheticMcDomainTypeRef(node.artifactTypeRef)) {
    return undefined;
  }
  return candidate;
}

/**
 * Map Mission Control tile key → company-artifact filterScope / instance id.
 * Prefer explicit ``mcDomainKey`` (#13 emit); fall back to label/scope regex.
 * Alias: use the same id for sidebar scope so queues match (SPA S4 / M1).
 */
export function resolveMcDomainArtifactInstanceId(
  structure: KryspaceControlBridgeStructure,
  domain: McDomainKey
): string | undefined {
  for (const group of structure.groups) {
    for (const node of group.nodes) {
      if (node.role !== 'domain') continue;
      if (nodeMcDomainKey(node) !== domain) continue;
      const byKey = domainNodeCandidate(node);
      if (byKey) return byKey;
    }
  }

  const patterns: Record<McDomainKey, RegExp> = {
    business: /business/i,
    customer: /customer/i,
    stakeholder: /stakeholder|resource/i,
  };
  const pattern = patterns[domain];

  for (const group of structure.groups) {
    for (const node of group.nodes) {
      const haystack = `${node.label} ${node.filterScope} ${node.key}`;
      if (node.role !== 'domain' || !pattern.test(haystack)) {
        continue;
      }
      const candidate = domainNodeCandidate(node);
      if (candidate) return candidate;
    }
  }
  return undefined;
}

/**
 * If ``scope`` is an MC tile keyword, rewrite to the structure CA id when known.
 * Leaves company / UUID / ``ca-*`` scopes unchanged.
 */
export function canonicalizeMcDomainScope(
  scope: string,
  structure: KryspaceControlBridgeStructure | undefined
): string {
  const trimmed = (scope ?? '').trim();
  if (!trimmed || !structure || !isMcDomainKey(trimmed)) {
    return trimmed || scope;
  }
  return resolveMcDomainArtifactInstanceId(structure, trimmed) ?? trimmed;
}

export function resolveGuidanceNodeArtifactInstanceId(
  node: GuidanceTreeNode & { filterScope?: string },
  structure?: KryspaceControlBridgeStructure
): string | undefined {
  if (node.filterScope && isCompanyArtifactInstanceId(node.filterScope)) {
    return node.filterScope;
  }
  if (isCompanyArtifactInstanceId(node.id)) {
    return node.id;
  }
  if (node.artifact && isCompanyArtifactInstanceId(node.artifact)) {
    return node.artifact;
  }
  if (!structure) return undefined;

  for (const group of structure.groups) {
    for (const root of group.nodes) {
      const stack = [...(root.children ?? []), root];
      for (const candidate of stack) {
        if (candidate.key === node.id || candidate.label === node.label) {
          return candidate.artifactInstanceId ?? candidate.filterScope;
        }
      }
    }
  }
  return undefined;
}
